/*
 * newsbot - Prompts settings page
 */

import { useState } from "react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";

import { toast } from "@components/hot-toast";
import Toast from "@components/notifications/Toast";
import { Section } from "./_components";
import { usePrompts, useUpdatePrompt, useResetPrompt } from "@api/queries";
import { PromptWithDefault } from "@/types";
import { classNames } from "@utils";

const PROMPT_LABELS: Record<string, { title: string; description: string }> = {
  extraction: {
    title: "Extraction Prompt",
    description: "Used to extract structured data from article text. The prompt receives article content and should return summary, key facts, entities, content type, suggested titles, and writing guidance.",
  },
  ranking: {
    title: "Ranking Prompt",
    description: "Used to evaluate and score news stories by importance. The prompt receives story data as JSON and should return importance scores and improved headlines.",
  },
};

function PromptEditor({ prompt }: { prompt: PromptWithDefault }) {
  const [content, setContent] = useState(prompt.content);
  const [isEditing, setIsEditing] = useState(false);

  const updateMutation = useUpdatePrompt();
  const resetMutation = useResetPrompt();

  const hasChanges = content !== prompt.content;

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({ name: prompt.name, content });
      toast.custom((t) => (
        <Toast type="success" body="Prompt saved successfully" t={t} />
      ));
      setIsEditing(false);
    } catch {
      toast.custom((t) => (
        <Toast type="error" body="Failed to save prompt" t={t} />
      ));
    }
  };

  const handleReset = async () => {
    try {
      await resetMutation.mutateAsync(prompt.name);
      setContent(prompt.default_content);
      toast.custom((t) => (
        <Toast type="success" body="Prompt reset to default" t={t} />
      ));
      setIsEditing(false);
    } catch {
      toast.custom((t) => (
        <Toast type="error" body="Failed to reset prompt" t={t} />
      ));
    }
  };

  const handleCancel = () => {
    setContent(prompt.content);
    setIsEditing(false);
  };

  const label = PROMPT_LABELS[prompt.name] || { title: prompt.name, description: "" };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            {label.title}
            {prompt.is_customized && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                Customized
              </span>
            )}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {label.description}
          </p>
        </div>
        {prompt.is_customized && !isEditing && (
          <button
            type="button"
            onClick={handleReset}
            disabled={resetMutation.isPending}
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Reset to default
          </button>
        )}
      </div>

      {isEditing ? (
        <>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={16}
            className="w-full px-3 py-2 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter prompt content..."
          />
          <div className="flex items-center justify-between mt-3">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Use <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{"{stories_json}"}</code> as placeholder for story data
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCancel}
                className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={updateMutation.isPending || !hasChanges}
                className={classNames(
                  "px-3 py-1.5 text-sm rounded-md",
                  hasChanges
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                )}
              >
                {updateMutation.isPending ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div
            className="w-full px-3 py-2 text-sm font-mono bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-300 max-h-48 overflow-y-auto whitespace-pre-wrap cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-850"
            onClick={() => setIsEditing(true)}
          >
            {content.length > 500 ? content.slice(0, 500) + "..." : content}
          </div>
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
            >
              Edit prompt
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function PromptsSettings() {
  const { data: prompts, isLoading, error } = usePrompts();

  if (isLoading) {
    return (
      <Section title="Prompts" description="Customize LLM prompts for ranking and analysis.">
        <div className="animate-pulse">Loading...</div>
      </Section>
    );
  }

  if (error) {
    return (
      <Section title="Prompts" description="Customize LLM prompts for ranking and analysis.">
        <div className="text-red-500">Failed to load prompts</div>
      </Section>
    );
  }

  return (
    <Section title="Prompts" description="Customize LLM prompts for ranking and analysis.">
      {prompts && prompts.length > 0 ? (
        <div>
          {prompts.map((prompt) => (
            <PromptEditor key={prompt.name} prompt={prompt} />
          ))}
        </div>
      ) : (
        <div className="text-gray-500 dark:text-gray-400">No prompts available</div>
      )}
    </Section>
  );
}

export default PromptsSettings;
